import { lazy, Suspense, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, AlertTriangle, X, BookOpen, History, KeyRound, Plus, UploadCloud } from 'lucide-react';

import {
  listSops,
  createSop,
  describeSopError,
  formatSopIdentifier,
  serviceLineLabel,
  type SopFamily,
  type SopListItem,
  type SopVersionState,
  type ServiceLineTag,
} from '@/api/sops.api';
import type { TipTapDoc } from '@/components/richtext/TipTapRenderer';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

// The TipTap editor bundle loads on demand — only when an editor opens the
// create modal, never for the read-only list.
const RichTextEditor = lazy(() => import('@/components/richtext/RichTextEditor'));

// The mass-upload wizard (docx parsing + AI applicability) is a heavier
// bundle — load it only when an editor opens it.
const MassUploadModal = lazy(() =>
  import('@/components/compliance/MassUploadModal').then((m) => ({ default: m.MassUploadModal })),
);

const SOP_FAMILIES: { value: SopFamily; label: string }[] = [
  { value: 'ADM', label: 'ADM — Administrative' },
  { value: 'ISO', label: 'ISO — ISO-Aligned' },
  { value: 'PAC', label: 'PAC — Packaging' },
  { value: 'QUA', label: 'QUA — Quality' },
  { value: 'REC', label: 'REC — Receiving / Shipping' },
  { value: 'SSOP', label: 'SSOP — Sanitation' },
  { value: 'KQM', label: 'KQM — Quality Manual' },
];

const ALL_SERVICE_LINES: ServiceLineTag[] = ['kwf', 'kwpl'];

/** Compliance authors (mirrors the server's `requireComplianceRole()` set). */
const EDITOR_ROLES = ['visionary', 'integrator', 'quality_lead'];

type ServiceLineFilter = 'all' | ServiceLineTag | 'untagged';

/**
 * Read-only list of all imported SOPs. For the FDA walkthrough, the inspector
 * needs to see in-scope (KWF) SOPs at a glance — service-line filter does
 * the heavy lifting. Family + state filters help with discovery.
 *
 * Filter state lives in component state (no URL persistence yet — Day 7 wires
 * deep-linkable URLs).
 */
export function SopListPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['compliance-sops'],
    queryFn: listSops,
  });

  const { state: authState } = useAuth();
  const isEditor =
    authState.status === 'authenticated' && EDITOR_ROLES.includes(authState.me.membership.role);

  const [createOpen, setCreateOpen] = useState(false);
  const [massUploadOpen, setMassUploadOpen] = useState(false);
  const [familyFilter, setFamilyFilter] = useState<'all' | SopFamily>('all');
  const [stateFilter, setStateFilter] = useState<'all' | SopVersionState>('all');
  const [serviceLineFilter, setServiceLineFilter] = useState<ServiceLineFilter>('all');

  const editorButtons = isEditor ? (
    <>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          setMassUploadOpen(true);
        }}
      >
        <UploadCloud className="size-4" />
        Mass upload
      </Button>
      <Button
        size="sm"
        onClick={() => {
          setCreateOpen(true);
        }}
      >
        <Plus className="size-4" />
        New SOP
      </Button>
    </>
  ) : null;

  const createModal =
    isEditor && createOpen ? (
      <CreateSopModal
        onClose={() => {
          setCreateOpen(false);
        }}
      />
    ) : null;

  const massUploadModal =
    isEditor && massUploadOpen ? (
      <Suspense fallback={null}>
        <MassUploadModal
          onClose={() => {
            setMassUploadOpen(false);
          }}
        />
      </Suspense>
    ) : null;

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((sop) => {
      if (familyFilter !== 'all' && sop.family !== familyFilter) return false;
      if (stateFilter !== 'all') {
        if (sop.latestVersion?.state !== stateFilter) return false;
      }
      if (serviceLineFilter === 'untagged') {
        if (sop.serviceLineTags.length > 0) return false;
      } else if (serviceLineFilter !== 'all') {
        if (!sop.serviceLineTags.includes(serviceLineFilter)) return false;
      }
      return true;
    });
  }, [data, familyFilter, stateFilter, serviceLineFilter]);

  const hasActiveFilter = familyFilter !== 'all' || stateFilter !== 'all' || serviceLineFilter !== 'all';

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load SOPs</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }

  const sops = data ?? [];
  if (sops.length === 0) {
    return (
      <>
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <FileText className="size-12 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No SOPs yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {isEditor ? 'Author a new SOP, or run' : 'Run'}{' '}
              <code className="rounded bg-kw-bg px-1.5 py-0.5 font-mono text-[12px]">
                pnpm compliance:import-sops
              </code>{' '}
              to load the existing corpus.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">{editorButtons}</div>
        </Card>
        {createModal}
        {massUploadModal}
      </>
    );
  }

  // Group filtered results by family for readability.
  const byFamily = new Map<string, SopListItem[]>();
  for (const sop of filtered) {
    const list = byFamily.get(sop.family) ?? [];
    list.push(sop);
    byFamily.set(sop.family, list);
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Standard Operating Procedures</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            {String(sops.length)} SOP{sops.length === 1 ? '' : 's'} total
            {filtered.length !== sops.length ? ` · ${String(filtered.length)} match the active filters` : ''}.
          </p>
        </div>
        {/* Auditor entry points — the master index is the inspector's
            preferred view (compact table, KWF-scoped, print-ready); the
            audit log answers "every change has an actor + reason." */}
        <div className="flex flex-wrap gap-2">
          <Link
            to="/compliance/audit-log"
            className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
          >
            <History className="size-4" />
            Audit log
          </Link>
          <Link
            to="/compliance/auditor-seats"
            className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
          >
            <KeyRound className="size-4" />
            Auditor seats
          </Link>
          <Link
            to="/compliance/audit-index"
            className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-cerulean-dark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
          >
            <BookOpen className="size-4" />
            Open master index
          </Link>
          {editorButtons}
        </div>
      </div>

      {createModal}
      {massUploadModal}

      {/* Filters bar */}
      <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center">
        <FilterSelect
          label="Family"
          value={familyFilter}
          onChange={(v) => {
            setFamilyFilter(v as 'all' | SopFamily);
          }}
          options={[
            { value: 'all', label: 'All families' },
            { value: 'ADM', label: 'ADM — Administrative' },
            { value: 'ISO', label: 'ISO — ISO-Aligned' },
            { value: 'PAC', label: 'PAC — Packaging' },
            { value: 'QUA', label: 'QUA — Quality' },
            { value: 'REC', label: 'REC — Receiving / Shipping' },
            { value: 'SSOP', label: 'SSOP — Sanitation' },
            { value: 'KQM', label: 'KQM — Quality Manual' },
          ]}
        />
        <FilterSelect
          label="State"
          value={stateFilter}
          onChange={(v) => {
            setStateFilter(v as 'all' | SopVersionState);
          }}
          options={[
            { value: 'all', label: 'All states' },
            { value: 'draft', label: 'Draft' },
            { value: 'pending_approval', label: 'Pending approval' },
            { value: 'approved', label: 'Approved' },
            { value: 'superseded', label: 'Superseded' },
          ]}
        />
        <FilterSelect
          label="Service line"
          value={serviceLineFilter}
          onChange={(v) => {
            setServiceLineFilter(v as ServiceLineFilter);
          }}
          options={[
            { value: 'all', label: 'All' },
            { value: 'kwf', label: 'KWF (audit scope)' },
            { value: 'kwpl', label: 'KWPL (out of scope)' },
            { value: 'untagged', label: 'Untagged' },
          ]}
        />
        {hasActiveFilter ? (
          <button
            type="button"
            onClick={() => {
              setFamilyFilter('all');
              setStateFilter('all');
              setServiceLineFilter('all');
            }}
            className="ml-auto inline-flex items-center gap-1 text-[12px] text-kw-cerulean hover:text-kw-cerulean-dark"
          >
            <X className="size-3" />
            Clear filters
          </button>
        ) : null}
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-[13px] text-kw-muted">No SOPs match the active filters.</Card>
      ) : (
        Array.from(byFamily.entries()).map(([family, list]) => (
          <section key={family} aria-labelledby={`family-${family}`} className="space-y-3">
            <h2 id={`family-${family}`} className="typo-h3 sticky top-16 z-10 bg-kw-bg py-1 md:top-16">
              {familyLabel(family)} ({list.length})
            </h2>
            <div className="space-y-2">
              {list.map((sop) => (
                <Link
                  key={sop.id}
                  to={`/compliance/sops/${sop.slug}`}
                  className="block rounded-kw-md border border-kw-border bg-white p-4 transition-shadow hover:shadow-kw-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[12px] text-kw-muted">
                        {formatSopIdentifier({
                          family: sop.family,
                          number: sop.number,
                          revision: sop.latestVersion?.revision,
                        })}
                      </p>
                      <p className="mt-0.5 truncate text-[15px] font-medium text-kw-shark">{sop.title}</p>
                      {sop.importedFromFilename ? (
                        <p className="mt-1 truncate text-[11px] text-kw-muted">
                          Imported from <span className="font-mono">{sop.importedFromFilename}</span>
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      {sop.latestVersion ? <StateBadge state={sop.latestVersion.state} /> : null}
                      <ServiceLineTags tags={sop.serviceLineTags} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

interface FilterOption {
  value: string;
  label: string;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: FilterOption[];
}) {
  const id = `filter-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-[12px] font-medium text-kw-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ServiceLineTags({ tags }: { tags: ServiceLineTag[] }) {
  if (tags.length === 0) {
    return (
      <span className="text-[10px] uppercase tracking-wide text-kw-ghost" title="No service line assigned">
        Untagged
      </span>
    );
  }
  return (
    <div className="flex gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className={
            tag === 'kwf'
              ? 'rounded-kw-full bg-kw-cerulean-light px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-kw-cerulean-dark'
              : 'rounded-kw-full bg-kw-emerald-light px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-kw-tag-text'
          }
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

function familyLabel(family: string): string {
  switch (family) {
    case 'ADM':
      return 'ADM — Administrative';
    case 'ISO':
      return 'ISO — ISO-Aligned Procedures';
    case 'PAC':
      return 'PAC — Packaging';
    case 'QUA':
      return 'QUA — Quality';
    case 'REC':
      return 'REC — Receiving / Shipping';
    case 'SSOP':
      return 'SSOP — Sanitation';
    case 'KQM':
      return 'KQM — Quality Manual';
    default:
      return family;
  }
}

function StateBadge({ state }: { state: SopVersionState }) {
  switch (state) {
    case 'approved':
      return <Badge variant="success">Approved</Badge>;
    case 'pending_approval':
      return <Badge variant="warning">Pending</Badge>;
    case 'superseded':
      return <Badge variant="neutral">Superseded</Badge>;
    case 'draft':
    default:
      return <Badge variant="info">Draft</Badge>;
  }
}

const EMPTY_DOC: TipTapDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

/**
 * Author a brand-new SOP and its first (draft) revision. Mounted fresh each
 * time it opens (so the lazy TipTap editor seeds cleanly and the form
 * resets). On success it navigates straight to the new SOP so the author can
 * keep editing / submit it for approval.
 */
function CreateSopModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [family, setFamily] = useState<SopFamily>('QUA');
  const [numberInput, setNumberInput] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<TipTapDoc | null>(null);
  const [tags, setTags] = useState<Set<ServiceLineTag>>(() => new Set());
  const [changeSummary, setChangeSummary] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const trimmedNumber = numberInput.trim();
      const parsedNumber = trimmedNumber === '' ? undefined : Number.parseInt(trimmedNumber, 10);
      return createSop({
        family,
        number: parsedNumber,
        title: title.trim(),
        contentJson: content ?? EMPTY_DOC,
        serviceLineTags: Array.from(tags),
        changeSummary: changeSummary.trim() === '' ? undefined : changeSummary.trim(),
        reasonForChange: reason.trim(),
      });
    },
    onSuccess: (detail) => {
      void queryClient.invalidateQueries({ queryKey: ['compliance-sops'] });
      toast.show('SOP created as a draft', { variant: 'success' });
      onClose();
      void navigate(`/compliance/sops/${detail.slug}`);
    },
  });

  function toggleTag(tag: ServiceLineTag): void {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  const numberValid = numberInput.trim() === '' || /^\d{1,4}$/.test(numberInput.trim());
  const canSubmit = title.trim().length > 0 && reason.trim().length > 0 && numberValid && !mutation.isPending;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate();
  }

  return (
    <Modal open onClose={onClose} title="New SOP" size="lg">
      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-1.5">
            <label htmlFor="sop-family" className="block text-[13px] font-medium text-kw-label">
              Family
            </label>
            <select
              id="sop-family"
              value={family}
              onChange={(e) => {
                setFamily(e.target.value as SopFamily);
              }}
              className="block w-full rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              {SOP_FAMILIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Number"
            name="sop-number"
            type="number"
            min={1}
            max={9999}
            inputMode="numeric"
            className="sm:w-28"
            placeholder="Auto"
            value={numberInput}
            onChange={(e) => {
              setNumberInput(e.target.value);
            }}
            hint="Blank = next in family"
          />
        </div>

        <Input
          label="Title"
          name="sop-title"
          required
          maxLength={300}
          placeholder="e.g. Incoming Material Inspection"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />

        <div>
          <span className="mb-1.5 block text-[13px] font-medium text-kw-label">Content</span>
          <Suspense
            fallback={
              <div className="rounded-kw-md border border-kw-border px-3.5 py-6 text-center text-[13px] text-kw-muted">
                Loading editor…
              </div>
            }
          >
            <RichTextEditor value={content} onChange={setContent} />
          </Suspense>
        </div>

        <div className="space-y-1.5">
          <span className="block text-[13px] font-medium text-kw-label">Service lines</span>
          <div className="flex flex-wrap gap-2">
            {ALL_SERVICE_LINES.map((tag) => (
              <label
                key={tag}
                className="flex cursor-pointer items-center gap-2 rounded-kw-md border border-kw-border px-3 py-2 text-[13px] hover:bg-kw-bg"
              >
                <input
                  type="checkbox"
                  checked={tags.has(tag)}
                  onChange={() => {
                    toggleTag(tag);
                  }}
                  className="size-4 accent-kw-cerulean"
                />
                <span className="font-medium text-kw-shark">{serviceLineLabel(tag)}</span>
              </label>
            ))}
          </div>
        </div>

        <Input
          label="Change summary"
          name="sop-change-summary"
          maxLength={500}
          placeholder="Optional — a short note shown in the revision history"
          value={changeSummary}
          onChange={(e) => {
            setChangeSummary(e.target.value);
          }}
        />

        <Input
          label="Reason for change"
          name="sop-reason"
          required
          maxLength={500}
          placeholder="e.g. new procedure required for the incoming-inspection line"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
          hint="Required by cGMP §111.180(c). Captured in the audit log."
        />

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeSopError(mutation.error, 'Could not create the SOP.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={!canSubmit}>
            Create draft
          </Button>
        </div>
      </form>
    </Modal>
  );
}
